/**
 * s234 — CH1-CHILDREN-ON-BENEFICIARIES (D-233.72, D-233.75(4), D-234.43, D-234.47).
 *
 * Every row filed as a child on Children (dependants.kind = 'child') is also a row of the Beneficiaries table,
 * linked to the child's OWN person (one record, D-124.L): the name and the date of birth are the same stored
 * answers on both screens. The Beneficiaries row has the same row id as the child's Children row, so the two
 * are found from each other without a lookup, and its "which kind" is the tile value `child`.
 *
 * The board (board 1 v443, `Main.dc.html`, the `benef` step) draws that row as: Which kind "Your child", the
 * name, "My child", Under 18?, date of birth. No Add row and no ×: a child is added or removed on Children.
 *
 * ONE source for "is this the child's row" and for what the row says — the screen, the completion input, the
 * essential and the layer all read these, never a copy.
 */
import { FIELD_KEYS } from "./constants/fields";
import { isChildForGovernmentForms } from "./constants/dependent-relationships";

/** The stored "which kind" of a child's row, as the layer's codec reads it (board: Which kind "Your child"). */
export const CHILD_BENEFICIARY_TILE_LABEL = "Your child";

/** What the row says a son, a daughter or an adult child is to the planner (board: "My child"). Drawn, never stored. */
export const MY_CHILD = "My child";

/**
 * The cells a child's Beneficiaries row carries, so every reader that lists rows from cells sees it. The mapped
 * ones hold no value of their own — the layer reads the child's person and the row's record (D-229.84) — and
 * `beneficiary_type` holds "Person" (it stays a cell; its gates keep the person's columns asked).
 */
export const CHILD_BENEFICIARY_CELL_KEYS: readonly string[] = [
  FIELD_KEYS.BENEFICIARY_TILE,
  FIELD_KEYS.BENEFICIARY_TYPE,
  FIELD_KEYS.BENEFICIARY_FIRST_NAME,
  FIELD_KEYS.BENEFICIARY_SURNAME,
  FIELD_KEYS.BENEFICIARY_RELATIONSHIP,
  FIELD_KEYS.BENEFICIARY_DOB,
  FIELD_KEYS.BENEFICIARY_MINOR,
];
export const CHILD_BENEFICIARY_TYPE = "Person";

/**
 * 🔴 Answers a child's Beneficiaries row never writes: which kind it is comes from Children, and a type other than
 * Person would close the date-of-birth gate and clear the CHILD's date of birth.
 * s236 (rule 158, D-235.105(2) — reverses D-234.47; EDIT-ANYWHERE-1 PR B): who they are is NO LONGER fixed — it is the
 * child's own person's relationship, edited on Children or Beneficiaries, one record. Adding or removing the child stays
 * on Children (beneficiaryDeletePlan, unchanged).
 */
export const CHILD_BENEFICIARY_FIXED_KEYS: readonly string[] = [
  FIELD_KEYS.BENEFICIARY_TILE,
  FIELD_KEYS.BENEFICIARY_TYPE,
];

/** Is this Beneficiaries row a child's row? From the row's own "which kind". Pure. */
export function isChildBeneficiaryRow(values: Readonly<Record<string, string | null | undefined>>): boolean {
  return (values[FIELD_KEYS.BENEFICIARY_TILE] ?? "") === CHILD_BENEFICIARY_TILE_LABEL;
}

/** The same question over a row's cells, keyed by field key. Pure. */
export function isChildBeneficiaryEntries(entries: ReadonlyArray<{ fieldKey?: string | null; value?: string | null }>): boolean {
  return entries.some((e) => e.fieldKey === FIELD_KEYS.BENEFICIARY_TILE && (e.value ?? "") === CHILD_BENEFICIARY_TILE_LABEL);
}

/**
 * What a child's row says they are (D-234.47): "My child" for a son, a daughter or an adult child — and for a
 * child whose relationship is not chosen yet (the row is filed as a child); every other relationship shows
 * itself ("Ward", "Stepchild", "Grandchild"). Pure.
 */
export function childBeneficiaryWho(relationship: string | null | undefined): string {
  const r = (relationship ?? "").trim();
  return !r || isChildForGovernmentForms(r) ? MY_CHILD : r;
}
